<script lang="ts" setup>
import { toRefs, ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { isBounce } from '@/lib/useServiceBounce';
import type { Menu } from '@/composables/useGnbMenu';

const props = defineProps<{
  menu: Menu[];
  mypage: Menu[];
  signOut: () => void;
  isAuthenticated: boolean;
}>();

const store = useStore();
const router = useRouter();
const { menu, mypage, signOut, isAuthenticated } = toRefs(props);
const menuKey = ref<number | 'mypage'>();

const client = computed(() => store.getters['auth/getClientInfo']());
const center = computed(() => store.getters['auth/getCenterInfo']());

const childrenRouter = computed(() => {
  if (menuKey.value === 'mypage') return mypage.value;
  return menu.value[menuKey.value!].children;
});

const childrenPosition = computed(() => {
  if (menuKey.value === 'mypage') return 'justify-end';
  return '';
});

const childrenOffsetX = computed(() => {
  const firstOffset = !isAuthenticated.value ? 320 : 270;
  const textLength = !isAuthenticated.value ? 0 : (client.value.name.length + center.value.name.length) * 2;
  if (menuKey.value === 'mypage') return { right: '20px' };
  return {
    'padding-left': `${firstOffset + 130 * menuKey.value! - textLength}px`,
  };
});

const isOpenGotcha = computed(() => {
  // const gotchaOpenCenter =
  // import.meta.env.VITE_ENV === "master"
  //   ? "62d501d545b113001cb20ed2"
  //   : "62a1729a6352c9001c985238";
  // return center.value.centerId === gotchaOpenCenter;
  return false;
});

const gotchaLink = 'https://bit.ly/3Fy5c8t';
const manualLink = 'https://krcodver.notion.site/krcodver/9867dc96cda84220a9d7ae489528c966';

onMounted(() => {
  router.beforeEach((to, from) => {
    menuKey.value = undefined;
  });
});
</script>

<template>
  <div
    role="banner"
    aria-label="데스크탑 메뉴"
    class="h-20 transition mx-auto"
    @mouseleave="menuKey = undefined"
  >
    <!-- 메뉴 -->
    <div class="container flex justify-between items-center h-full z-30">
      <router-link to="/">
        <img
          class="logo"
          src="@/assets/image/dpost-logo.svg"
          alt="로고"
        />
      </router-link>

      <!-- 라우터 -->
      <nav
        class="router"
        role="navigation"
      >
        <router-link
          v-for="(item, index) in menu"
          :key="item.title"
          :to="item.link"
          class="router__item"
          @mouseover="menuKey = index"
          v-show="item.isRender !== false"
        >
          <div>
            {{ item.title }}
          </div>
        </router-link>
      </nav>

      <div class="flex items-center gap-4 text-xs font-semibold">
        <button
          @click.stop="
            router.push('/service');
            isBounce++;
          "
          class="faq-button"
        >
          상담문의
        </button>

        <div
          v-if="!isAuthenticated"
          class="flex"
        >
          <button
            @click="router.push('/signin')"
            class="flex flex-row gap-2.5"
          >
            로그인
            <div class="font-thin">|</div>
            회원가입
          </button>
        </div>

        <div
          v-else
          @mouseover="menuKey = 'mypage'"
          @click="router.push('/mypage')"
          class="flex items-center gap-2 cursor-pointer"
        >
          <div>{{ client.name }} 님</div>
          <div class="font-thin">|</div>
          <div class="flex items-center gap-1">
            <div>{{ center.name }}</div>
            <img src="@/assets/image/profile.svg" />
          </div>
        </div>
      </div>
    </div>

    <!-- 하위메뉴 -->
    <div
      class="w-full bg-white border-y border-black2 h-16"
      v-if="typeof menuKey !== 'undefined' && childrenRouter?.length"
    >
      <nav
        role="navigation"
        class="container h-full flex gap-8"
        :class="`${childrenPosition}`"
        :style="childrenOffsetX"
      >
        <router-link
          v-for="(item, index) in childrenRouter"
          :key="index"
          :to="item.link"
          class="child-router__item"
        >
          {{ item.title }}
        </router-link>
        <a
          :href="gotchaLink"
          target="_blank"
          v-if="menuKey === 0 && isOpenGotcha"
          class="child-router__item"
          >세차접수</a
        >
        <a
          :href="manualLink"
          target="_blank"
          v-if="menuKey === 2"
          class="child-router__item"
          >이용가이드</a
        >

        <button
          v-if="menuKey === 'mypage'"
          @click="signOut()"
          class="hover:text-primary transition-all"
        >
          로그아웃
        </button>
      </nav>
    </div>
  </div>
</template>

<style lang="scss" scoped>
button {
  @apply bg-transparent h-auto;
  color: inherit;
}
.router {
  @apply h-full grid grid-cols-4 gap-10;

  &__item {
    @apply flex flex-col justify-center items-center;
    @apply text-lg font-semibold;
    @apply border-b-4 border-transparent pt-1;
    @apply hover:text-primary hover:transition;
  }
}

.child-router__item {
  @apply hover:text-primary font-semibold transition-all h-full flex flex-col justify-center;
}

a {
  &.router-link-exact-active {
    @apply text-primary;
  }
}

.faq-button {
  @apply bg-darkblue text-white px-5 py-2.5 rounded;
}
</style>
